<template>
  <div class="base-table" :class="{ 'table-responsive': responsive, 'sticky-header': stickyHeader }" ref="tableContainerRef">
    <slot name="table-header"></slot>
    <table
      class="table"
      :class="{ 'table-hover': hover }"
      v-sortable-row="sortableRowOptions"
      v-sortable-col="sortableColOptions"
      ref="tableRef"
      :key="`key-${keyChange}`"
    >
      <thead>
        <tr v-if="busy">
          <th :colspan="columns.length + 1 || 10" class="table-busy">
            <div class="loading-header" />
          </th>
        </tr>
        <tr v-else>
          <th class="base-table-checkbox-container" v-if="checkbox">
            <slot name="select-all-row">
              <input class="form-check-input" type="checkbox" @change="onSelectAllRow" ref="selectAllCheckbox" />
            </slot>
          </th>
          <th
            v-for="(col, index) in columns"
            :key="`column-${col.key}`"
            class="dragable-col"
            :class="[col.key, { 'dynamic-filter': col.sortable && sortAndFilter }]"
            :style="[{ pointerEvents: col.label ? 'all' : col.column_alias ? 'all' : 'none' }, { display: col.selected == false ? 'none' : '' }]"
            :id="'' + col.column_id"
          >
            <div class="sorting-content-container" v-if="(col.sortable && !sortAndFilter) || !tableViewMode">
              <input type="checkbox" :id="`sorting-${col.key}`" @change="onSort($event, col)" class="d-none sorting-checkbox" />
              <label :for="!col.sortable ? '' : `sorting-${col.key}`" class="w-100">
                <div class="d-flex align-items-center">
                  <div class="handle-dragable" v-if="sortableCol">
                    <div v-if="col.key != 'action'">
                      <DragableIcon class="dragable-icon handle-dragable-col" />
                    </div>
                  </div>
                  <div class="column-content">
                    <slot :name="`head(${col.key})`" :item="col" :index="index">{{ col.label || '' }}</slot>
                  </div>
                  <div v-if="col.sortable && !sortAndFilter" class="sorting-icon-container">
                    <SortingIcon class="sorting-icon" />
                    <SortingIconUp class="sorting-icon-up" />
                    <SortingIconDown class="sorting-icon-down" />
                  </div>
                </div>
              </label>
            </div>
            <div
              class="sorting-content-container"
              v-else-if="(col.sortable && sortAndFilter) || tableViewMode"
              :class="{ 'action-filter-dynamic': selectColumnFilter.some((e: any) => e.Key == col.key) || keySorting == col.key }"
            >
              <input type="checkbox" :id="`sorting-${col.key}`" class="form-check-input d-none sorting-checkbox" />
              <label :for="`sorting-${col.key}`" class="w-100">
                <div class="d-flex align-items-center">
                  <div class="handle-dragable" v-if="sortableCol">
                    <div v-if="col.key != 'action'"><DragableIcon class="dragable-icon handle-dragable-col" /></div>
                  </div>
                  <div class="column-content">
                    <slot :name="`head(${col.key})`" :item="col" :index="index">{{ String(col.label) || '' }}</slot>
                  </div>
                  <div v-if="col.type_id != 2" class="sorting-icon-container" @click="onTableColumnProperty($event, col)">
                    <FilterIcon v-if="selectColumnFilter.some((e: any) => e.Key == col.key)" class="filter-icon" />
                    <ArrowDownIcon v-else class="arrow-down-icon" />
                    <ArrowSortASCIcon v-if="keySorting == col.key" class="arrow-down-icon" />
                  </div>
                </div>
              </label>
            </div>
            <div class="dragable-col" v-else>
              <div class="d-flex align-items-center">
                <div class="handle-dragable" v-if="sortableCol">
                  <div v-if="col.key != 'action'">
                    <DragableIcon class="dragable-icon handle-dragable-col" />
                  </div>
                </div>
                <div class="column-content">
                  <slot :name="`head(${col.key})`" :item="col" :index="index">{{ String(col.label) || '' }}</slot>
                </div>
              </div>
            </div>
          </th>
        </tr>
      </thead>

      <!-- ================== tbody ปรับปรุงให้ copy ได้ ================== -->
      <tbody>
        <tr v-if="busy">
          <td :colspan="columns?.length + 1 || 10" class="table-busy">
            <div class="table-busy-content">
              <slot name="busy">
                <div>{{ `...${t('base_table.loading').toLocaleLowerCase()}...` }}</div>
              </slot>
            </div>
          </td>
        </tr>

        <tr
          class="dragable-row"
          v-for="(row, rowIndex) in displayedData"
          v-else-if="displayedData && displayedData.length"
          :key="staticKey ? staticKey : `row-${rowIndex}`"
          @click="onRowClick($event, row)"
          @mousedown="onRowMiddleClick($event, row)"
          @contextmenu.prevent="onRowRightClick(row)"
        >
          <!-- Checkbox per row -->
          <td class="base-table-checkbox-container" @click.stop v-if="checkbox">
            <slot name="select-row" :row="row">
              <input class="form-check-input" type="checkbox" @change="onSelectRow(row)" />
            </slot>
          </td>

          <!-- Columns -->
          <td v-for="(col, colIndex) in columns" :key="`row-${rowIndex}-col-${colIndex}`" :class="col.key">
            <div class="column-wrapper">
              <!-- Drag handle -->
              <div class="handle-dragable" v-if="sortableRow">
                <DragableIcon class="dragable-icon handle-dragable-col" />
              </div>

              <!-- Column content (selectable) -->
              <div class="column-content" @mousedown.stop @pointerdown.stop>
                <slot :name="`cell(${col.key})`" :item="row" :index="rowIndex">
                  {{ displayCellValue(row[col.key]) }}
                </slot>
              </div>
            </div>
          </td>
        </tr>

        <tr class="empty-table" v-else>
          <td :colspan="columns?.length + 1 || 10">
            <slot name="empty">
              <div class="d-inline-block text-center">
                <EmptyTableIcon />
                <slot name="empty-label">
                  <div class="mt-2 empty-table-label">{{ t('base_table.noItems') }}</div>
                </slot>
              </div>
            </slot>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="modal-filter-sorting">
      <base-filter-table
        :is-open-filter="isOpenFilter"
        :table-column="columns"
        :select-column="selectTableColumn"
        :table-id="tableId"
        :select-column-filter="selectColumnFilter"
        :select-column-sorting="selectColumnSorting"
        @on-close-filter="onCloseTableFilter"
        @on-data-filter="emit('on-data-filter', $event)"
        @on-remove-item="emit('on-remove-item', $event)"
        @on-clear-data-filter="emit('on-clear-data-filter', $event)"
        @on-selected-sorting="emit('on-selected-sorting', $event)"
        @force-close-filter="onForceCloseTableFilter"
        @on-force-close-sort="onForceCloseTableSort"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import SortingIconUp from '@/assets/icons/base/table/SortingIconUp.vue';
import SortingIconDown from '@/assets/icons/base/table/SortingIconDown.vue';
import SortingIcon from '@/assets/icons/base/table/SortingIcon.vue';
import EmptyTableIcon from '@/assets/icons/base/table/EmptyTableIcon.vue';
import DragableIcon from '@/assets/icons/base/table/DragableIcon.vue';
import ArrowDownIcon from '@/assets/icons/base/table/ArrowDown.vue';
import ArrowSortASCIcon from '@/assets/icons/base/table/ArrowSortASC.vue';
import FilterIcon from '@/assets/icons/base/table/FilterIcon.vue';
import { displayCellValue } from '@/utils/formatCellValue';

import { sortableRow as vSortableRow, sortableCol as vSortableCol } from './hooks/directive';
import useBaseTable from './hooks/useBaseTable';
import useResizeColumn from './hooks/resizeable';
import BaseFilterTable from '../base-filter-table/BaseFilterTable.vue';
import useDynamicFilter from './hooks/useDynamicFilter';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  columns: Array<BaseTable.TableColumn>;
  data?: Array<Record<string, any>>;
  hover?: boolean;
  checkbox?: boolean;
  responsive?: boolean;
  stickyHeader?: boolean;
  sortableRow?: boolean;
  sortableCol?: boolean;
  resizeCol?: boolean;
  currentPage?: number;
  perPages?: number;
  rowSelectVariant?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'light' | 'dark' | 'light-blue';
  busy?: boolean;
  selectAll?: boolean;
  sortAndFilter?: boolean;
  tableId?: number;
  selectColumnFilter?: any;
  selectColumnSorting?: any;
  tableViewMode?: boolean;
  isDynamictable?: boolean;
  staticKey?: string;
  aHref?: string | null;
}

const props = defineProps<Props>();
const emit = defineEmits([
  'row-change',
  'col-change',
  'sorting-change',
  'row-click',
  'select-all-row',
  'unselect-all-row',
  'row-select',
  'on-data-filter',
  'on-remove-item',
  'on-clear-data-filter',
  'on-selected-sorting',
  'right-click-row',
  'middle-click-row',
]);

const {
  tableRef,
  tableContainerRef,
  selectAllCheckbox,
  sortableRowOptions,
  sortableColOptions,
  keyChange,
  displayedData,
  onSelectAllRow,
  onSelectRow,
  onSort,
  onRowClick,
  onRowRightClick,
  onRowMiddleClick,
} = useBaseTable(props);

const { isOpenFilter, selectTableColumn, keySorting, onTableColumnProperty, onCloseTableFilter, onForceCloseTableFilter, onForceCloseTableSort } =
  useDynamicFilter(props);

useResizeColumn(props);
</script>

<style lang="scss" scoped>
@import '@/assets/styles/base/base-table';
@import '@/assets/styles/base/filter-table/filter-in-table.scss';

/* ================== ปรับ CSS tbody เพื่อเลือกข้อความ ================== */
:deep(.base-table) {
  tbody tr td .handle-dragable,
  tbody tr td .base-table-checkbox-container {
    user-select: none !important;
  }

  tbody tr td .column-content {
    user-select: text !important;
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style>
